import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./BlockFilter.module.scss";
import { Button, ThemeButton } from "../Button/Button";
import MultiDropdown, { DropdownOption } from "../Dropdown/MultiDropdown";
import { IResponseGetClubs } from "../../../types/club";
import { getClubs } from "../../../api/clubs";
import { getAppearance } from "../../../api/appearance";
import { IAppearance } from "../../../types/appearance";
import { getServices } from "../../../api/services";
import { IService } from "../../../types/service";
import { getLanguages } from "../../../api/language";
import { ILangModel } from "../../../types/lang";
import { FilterParametrs } from "../../FilterParametrs/FilterParametrs";
import { getUsersFilters } from "../../../api/user";
import { IGirlItem } from "../../GirlItem/GirlItem";
import { IResponseGetUsers } from "../../../types/user";
import { transformUsersToGirlItems } from "../../../helpers/transform-user-to-models";
export interface IBlockFilter {
  count: number;
  setGirls: (value: IGirlItem[]) => void;
}

export const DEFAULT: Record<string, [number, number]> = {
  age: [18, 70],
  bust: [1, 12],
  height: [120, 220],
  weight: [35, 150],
};

export const BlockFilter: FC<IBlockFilter> = ({ count, setGirls }) => {
  const [clubs, setClubs] = useState<DropdownOption[]>([]);
  const [services, setServices] = useState<DropdownOption[]>([]);
  const [appearance, setAppearance] = useState<DropdownOption[]>([]);
  const [languages, setLanguages] = useState<DropdownOption[]>([]);

  const [age, setAge] = useState<[number, number]>(DEFAULT.age);
  const [bust, setBust] = useState<[number, number]>(DEFAULT.bust);
  const [height, setHeight] = useState<[number, number]>(DEFAULT.height);
  const [weight, setWeight] = useState<[number, number]>(DEFAULT.weight);

  const [clubsSelected, setClubsSelected] = useState<number[]>([]);
  const [servicesSelected, setServicesSelected] = useState<number[]>([]);
  const [appearanceSelected, setAppearanceSelected] = useState<number[]>([]);
  const [languagesSelected, setLanguagesSelected] = useState<number[]>([]);

  const location = useLocation();
  async function fetchData() {
    const clubsData: IResponseGetClubs = await getClubs();
    setClubs(clubsData.data.map((val) => ({ id: val.id, name: val.club })));

    const appearanceData: IAppearance[] = await getAppearance();
    setAppearance(
      appearanceData.map((val) => ({ id: val.id, name: val.appearance })),
    );

    const servicesData: IService[] = await getServices();
    setServices(
      servicesData
        .filter((item) => item.isDeleted === false)
        .map((val) => ({ id: val.id, name: val.serviceName })),
    );

    const languagesData: ILangModel[] = await getLanguages();
    setLanguages(
      languagesData.map((val) => ({ id: val.id, name: val.language })),
    );
  }
  const navigate = useNavigate();

  const handleSearch = async () => {
    const query = new URLSearchParams();

    if (servicesSelected.length > 0) {
      query.append("services", servicesSelected.join(","));
    }
    if (clubsSelected.length > 0) {
      query.append("clubs", clubsSelected.join(","));
    }
    if (appearanceSelected.length > 0) {
      query.append("appearance", appearanceSelected.join(","));
    }
    if (languagesSelected.length > 0) {
      query.append("languages", languagesSelected.join(","));
    }

    if (age[0] !== DEFAULT.age[0] || age[1] !== DEFAULT.age[1]) {
      query.append("age[min]", age[0].toString());
      query.append("age[max]", age[1].toString());
    }
    if (weight[0] !== DEFAULT.weight[0] || weight[1] !== DEFAULT.weight[1]) {
      query.append("weight[min]", weight[0].toString());
      query.append("weight[max]", weight[1].toString());
    }
    if (bust[0] !== DEFAULT.bust[0] || bust[1] !== DEFAULT.bust[1]) {
      query.append("bust[min]", bust[0].toString());
      query.append("bust[max]", bust[1].toString());
    }
    if (height[0] !== DEFAULT.height[0] || height[1] !== DEFAULT.height[1]) {
      query.append("height[min]", height[0].toString());
      query.append("height[max]", height[1].toString());
    }
    const users: IResponseGetUsers[] = await getUsersFilters(query.toString());
    const girlsData = transformUsersToGirlItems(users);
    setGirls(girlsData);

    navigate({ search: query.toString() }, { replace: true });
  };

  const handleClearFilters = () => {
    setAge(DEFAULT.age);
    setBust(DEFAULT.bust);
    setHeight(DEFAULT.height);
    setWeight(DEFAULT.weight);

    setClubsSelected([]);
    setServicesSelected([]);
    setAppearanceSelected([]);
    setLanguagesSelected([]);

    const query = new URLSearchParams();
    navigate({ search: query.toString() });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.toString()) {
      const services = query.get("services")?.split(",").map(Number) || [];
      const clubs = query.get("clubs")?.split(",").map(Number) || [];
      const appearance = query.get("appearance")?.split(",").map(Number) || [];
      const languages = query.get("languages")?.split(",").map(Number) || [];
      const ageMin = query.get("age[min]")
        ? Number(query.get("age[min]"))
        : DEFAULT.age[0];
      const ageMax = query.get("age[max]")
        ? Number(query.get("age[max]"))
        : DEFAULT.age[1];
      const weightMin = query.get("weight[min]")
        ? Number(query.get("weight[min]"))
        : DEFAULT.weight[0];
      const weightMax = query.get("weight[max]")
        ? Number(query.get("weight[max]"))
        : DEFAULT.weight[1];
      const bustMin = query.get("bust[min]")
        ? Number(query.get("bust[min]"))
        : DEFAULT.bust[0];
      const bustMax = query.get("bust[max]")
        ? Number(query.get("bust[max]"))
        : DEFAULT.bust[1];
      const heightMin = query.get("height[min]")
        ? Number(query.get("height[min]"))
        : DEFAULT.height[0];
      const heightMax = query.get("height[max]")
        ? Number(query.get("height[max]"))
        : DEFAULT.height[1];

      setServicesSelected(services);
      setClubsSelected(clubs);
      setAppearanceSelected(appearance);
      setLanguagesSelected(languages);
      setAge([ageMin, ageMax]);
      setWeight([weightMin, weightMax]);
      setBust([bustMin, bustMax]);
      setHeight([heightMin, heightMax]);
    }
  }, [location.search]);

  return (
    <div>
      <div className={styles.filters_container}>
        <div className={styles.filters}>
          <MultiDropdown
            selectedOptions={servicesSelected}
            options={services}
            onChange={setServicesSelected}
            placeholder="Select services"
          />
          <MultiDropdown
            selectedOptions={clubsSelected}
            options={clubs}
            onChange={setClubsSelected}
            placeholder="Select clubs"
          />
          <MultiDropdown
            selectedOptions={appearanceSelected}
            options={appearance}
            onChange={setAppearanceSelected}
            placeholder="Select appearance"
          />
          <MultiDropdown
            selectedOptions={languagesSelected}
            options={languages}
            onChange={setLanguagesSelected}
            placeholder="Select language"
          />
          <FilterParametrs
            setAge={setAge}
            setBust={setBust}
            setHeight={setHeight}
            setWeight={setWeight}
          />
        </div>
        <div className={styles.buttons}>
          <Button onClick={handleSearch}>Suche</Button>
          <Button onClick={handleClearFilters} theme={ThemeButton.CANCEL}>
            {" "}
            Filter reinigen
          </Button>
        </div>
      </div>
      <h3 className={styles.title}>Fragebögen gefunden: {count}</h3>
    </div>
  );
};
